import { Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import {
  CONDITIONAL_OPERATORS,
  ConditionalOperator,
  FormConditional,
  FormField,
  FormSchemaModel,
  FormShowWhen,
  collectFieldReferences,
  fieldReferenceLabel,
  resolveShowWhenOperator,
  serializeShowWhen
} from '../../../shared/dynamic-form/form-schema.models';

interface SelectOption<T = string> {
  label: string;
  value: T;
}

@Component({
  standalone: true,
  selector: 'ain-form-builder-visibility-panel',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    AinFormFieldComponent,
    InfoHintComponent
  ],
  template: `
    <section class="logic-panel">
      <ain-info-hint
        [titleKey]="'dynamic.builder.visibility.infoTitle'"
        [descriptionKey]="'dynamic.builder.visibility.infoDescription'"
        [bulletKeys]="['dynamic.builder.visibility.infoBullet']" />

      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="enabled()"
          [disabled]="!canEdit()"
          (ngModelChange)="toggleEnabled($event)" />
        <label>{{ t('dynamic.builder.visibility.enable') }}</label>
      </div>

      @if (enabled()) {
        <ain-form-field [label]="t('dynamic.builder.visibility.drivingField')">
          <p-select
            [options]="drivingFieldOptions()"
            optionLabel="label"
            optionValue="value"
            [ngModel]="showWhen()?.field ?? ''"
            [disabled]="!canEdit()"
            [filter]="true"
            appendTo="body"
            (ngModelChange)="updateDrivingField($event)" />
        </ain-form-field>

        <ain-form-field [label]="t('dynamic.builder.visibility.operator')">
          <p-select
            [options]="operatorOptions()"
            optionLabel="label"
            optionValue="value"
            [ngModel]="operator()"
            [disabled]="!canEdit()"
            appendTo="body"
            (ngModelChange)="updateOperator($event)" />
        </ain-form-field>

        @if (needsCompareValue()) {
          @if (drivingField()?.options?.length) {
            <ain-form-field [label]="t('dynamic.builder.visibility.compareValue')">
              <p-select
                [options]="drivingOptionValues()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="compareValue()"
                [disabled]="!canEdit()"
                appendTo="body"
                (ngModelChange)="updateCompareValue($event)" />
            </ain-form-field>
          } @else if (isBooleanDrivingField()) {
            <ain-form-field [label]="t('dynamic.builder.visibility.compareValue')">
              <p-select
                [options]="booleanOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="compareValue()"
                [disabled]="!canEdit()"
                appendTo="body"
                (ngModelChange)="updateCompareValue($event)" />
            </ain-form-field>
          } @else if (isNumericDrivingField()) {
            <ain-form-field [label]="t('dynamic.builder.visibility.compareValue')">
              <p-inputNumber
                [ngModel]="numericCompareValue()"
                [disabled]="!canEdit()"
                [useGrouping]="false"
                (ngModelChange)="updateCompareValue($event)" />
            </ain-form-field>
          } @else {
            <ain-form-field [label]="t('dynamic.builder.visibility.compareValue')">
              <input
                pInputText
                [ngModel]="compareValue()"
                [disabled]="!canEdit()"
                (ngModelChange)="updateCompareValue($event)" />
            </ain-form-field>
          }
        }

        @if (showRequireWhenVisible()) {
          <div class="checkbox-row">
            <p-checkbox
              binary
              [ngModel]="!!conditional()?.requireWhenVisible"
              [disabled]="!canEdit()"
              (ngModelChange)="updateRequireWhenVisible($event)" />
            <label>{{ t('dynamic.builder.visibility.requireWhenVisible') }}</label>
          </div>
        }

        @if (canEdit()) {
          <button pButton type="button" severity="secondary" text icon="pi pi-times" [label]="t('dynamic.builder.visibility.clear')" (click)="clearConditional()"></button>
        }
      }
    </section>
  `,
  styles: [`
    .logic-panel { display: grid; gap: 10px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
  `]
})
export class FormBuilderVisibilityPanelComponent {
  private readonly localization = inject(LocalizationService);

  readonly schema = input.required<FormSchemaModel>();
  readonly culture = input.required<string>();
  readonly canEdit = input(true);
  readonly excludeFieldKey = input<string | undefined>(undefined);
  readonly conditional = input<FormConditional | undefined>(undefined);
  readonly showRequireWhenVisible = input(true);

  readonly conditionalChange = output<FormConditional | undefined>();

  readonly enabled = computed(() => !!this.conditional()?.showWhen?.field);
  readonly showWhen = computed(() => this.conditional()?.showWhen);
  readonly operator = computed(() => resolveShowWhenOperator(this.showWhen()));

  readonly drivingFieldOptions = computed<SelectOption[]>(() =>
    collectFieldReferences(this.schema(), this.excludeFieldKey()).map(field => ({
      label: fieldReferenceLabel(field, this.culture()),
      value: field.key
    }))
  );

  readonly drivingField = computed(() => {
    const key = this.showWhen()?.field;
    if (!key) return null;
    return collectFieldReferences(this.schema()).find(field => field.key === key) ?? null;
  });

  readonly operatorOptions = computed<SelectOption<ConditionalOperator>[]>(() =>
    CONDITIONAL_OPERATORS.map(value => ({
      value,
      label: this.t(`dynamic.builder.visibility.operator.${value}`)
    }))
  );

  readonly drivingOptionValues = computed(() =>
    (this.drivingField()?.options ?? []).map(option => ({
      label: option.value,
      value: option.value
    }))
  );

  needsCompareValue(): boolean {
    return !['isempty', 'isnotempty'].includes(this.operator());
  }

  isBooleanDrivingField(): boolean {
    return ['boolean', 'checkbox'].includes(this.drivingField()?.type ?? '');
  }

  isNumericDrivingField(): boolean {
    return ['number', 'rating'].includes(this.drivingField()?.type ?? '');
  }

  compareValue(): unknown {
    const showWhen = this.showWhen();
    return showWhen?.value ?? showWhen?.eq ?? showWhen?.neq ?? showWhen?.in ?? showWhen?.notIn ?? '';
  }

  numericCompareValue(): number | null {
    const value = this.compareValue();
    return typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : null;
  }

  booleanOptions(): SelectOption[] {
    return [
      { label: this.t('common.yes'), value: 'true' },
      { label: this.t('common.no'), value: 'false' }
    ];
  }

  toggleEnabled(value: boolean): void {
    if (!value) {
      this.conditionalChange.emit(undefined);
      return;
    }
    const first = this.drivingFieldOptions()[0]?.value;
    if (!first) return;
    this.conditionalChange.emit({
      showWhen: serializeShowWhen({ field: first, operator: 'eq', value: '' }),
      requireWhenVisible: this.conditional()?.requireWhenVisible
    });
  }

  updateDrivingField(fieldKey: string): void {
    this.conditionalChange.emit({
      ...this.conditional(),
      showWhen: serializeShowWhen({
        field: fieldKey,
        operator: this.operator(),
        value: this.compareValue()
      })
    });
  }

  updateOperator(operator: ConditionalOperator): void {
    const showWhen = this.showWhen();
    if (!showWhen?.field) return;
    this.conditionalChange.emit({
      ...this.conditional(),
      showWhen: serializeShowWhen({
        field: showWhen.field,
        operator,
        value: ['isempty', 'isnotempty'].includes(operator) ? undefined : this.compareValue()
      })
    });
  }

  updateCompareValue(value: unknown): void {
    const showWhen = this.showWhen();
    if (!showWhen?.field) return;
    this.conditionalChange.emit({
      ...this.conditional(),
      showWhen: serializeShowWhen({
        field: showWhen.field,
        operator: this.operator(),
        value
      })
    });
  }

  updateRequireWhenVisible(value: boolean): void {
    this.conditionalChange.emit({
      ...this.conditional(),
      showWhen: this.showWhen(),
      requireWhenVisible: value
    });
  }

  clearConditional(): void {
    this.conditionalChange.emit(undefined);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
